import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, Plus, Edit, Archive, ChevronDown, ChevronUp, Sparkles, FileText, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';

// ---------------------------------------------------------------------------
// Constants (mirrored from backend legal_knowledge.py)
// ---------------------------------------------------------------------------
const BUNDESLAND_OPTIONS = [
  { value: 'BY', label: 'Bayern' },
  { value: 'BW', label: 'Baden-Württemberg' },
  { value: 'BE', label: 'Berlin' },
  { value: 'BB', label: 'Brandenburg' },
  { value: 'HB', label: 'Bremen' },
  { value: 'HH', label: 'Hamburg' },
  { value: 'HE', label: 'Hessen' },
  { value: 'MV', label: 'Mecklenburg-Vorpommern' },
  { value: 'NI', label: 'Niedersachsen' },
  { value: 'NW', label: 'Nordrhein-Westfalen' },
  { value: 'RP', label: 'Rheinland-Pfalz' },
  { value: 'SL', label: 'Saarland' },
  { value: 'SN', label: 'Sachsen' },
  { value: 'ST', label: 'Sachsen-Anhalt' },
  { value: 'SH', label: 'Schleswig-Holstein' },
  { value: 'TH', label: 'Thüringen' },
];

const KATEGORIE_OPTIONS = [
  'Bauen & Wohnen',
  'Meldewesen & Bürgerservice',
  'Kfz & Verkehr',
  'Familie & Kinder',
  'Gewerbe & Wirtschaft',
  'Umwelt & Abfall',
  'Tiere',
  'Soziales & Gesundheit',
  'Ordnung & Sicherheit',
  'Freizeit & Kultur',
];

const STATUS_OPTIONS = ['active', 'draft', 'review_needed', 'archived'];
const EBENE_OPTIONS = ['gemeinde', 'landkreis', 'mixed', 'bund', 'land'];

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  review_needed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  archived: 'bg-muted text-muted-foreground',
};

const STATUS_LABELS = {
  active: 'Aktiv',
  draft: 'Entwurf',
  review_needed: 'Prüfung nötig',
  archived: 'Archiviert',
};

const EMPTY_FORM = {
  id: '',
  thema: '',
  kategorie: '',
  keywords: '',
  rechtsgrundlage: '',
  zusammenfassung: '',
  einschraenkungen: '',
  gemeinde_aktion: '',
  url_gesetz: '',
  url_weiterfuehrend: '',
  ebene: 'mixed',
  status: 'draft',
  bundesland: 'BY',
};

// ---------------------------------------------------------------------------
// Component (isDark from AdminDashboard to match Dokumente tab styles)
// ---------------------------------------------------------------------------
export default function LandesrechtTab({ isDark = false }) {
  // --- Bundesland selector ---
  const [bundesland, setBundesland] = useState('BY');

  // --- Action loading states ---
  const [seedLoading, setSeedLoading] = useState(false);
  const [ingestLoading, setIngestLoading] = useState(false);

  // --- Result feedback ---
  const [seedResult, setSeedResult] = useState(null);
  const [ingestResult, setIngestResult] = useState(null);

  // --- Freshness data ---
  const [freshness, setFreshness] = useState(null);
  const [freshnessLoading, setFreshnessLoading] = useState(false);
  const [freshnessExpanded, setFreshnessExpanded] = useState(false);

  // --- Topics list ---
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [filterKategorie, setFilterKategorie] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // --- Sheet (create / edit) ---
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState('create'); // 'create' | 'edit'
  const [sheetTab, setSheetTab] = useState('smart-paste'); // 'smart-paste' | 'manual'
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formSaving, setFormSaving] = useState(false);

  // --- Smart paste ---
  const [smartRawText, setSmartRawText] = useState('');
  const [smartQuestion, setSmartQuestion] = useState('');
  const [smartParsing, setSmartParsing] = useState(false);

  // --- Archive confirmation ---
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------
  const fetchTopics = useCallback(async () => {
    setTopicsLoading(true);
    try {
      const params = new URLSearchParams({ bundesland });
      if (filterKategorie && filterKategorie !== 'all') params.set('kategorie', filterKategorie);
      if (filterStatus && filterStatus !== 'all') params.set('topic_status', filterStatus);
      const res = await apiClient.get(`/provider/legal/topics?${params}`);
      setTopics(res.data.topics ?? res.data ?? []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Laden der Themen');
    } finally {
      setTopicsLoading(false);
    }
  }, [bundesland, filterKategorie, filterStatus]);

  const fetchFreshness = useCallback(async () => {
    setFreshnessLoading(true);
    try {
      const res = await apiClient.get(`/provider/legal/freshness?bundesland=${bundesland}`);
      setFreshness(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Laden der Frische-Daten');
    } finally {
      setFreshnessLoading(false);
    }
  }, [bundesland]);

  useEffect(() => {
    fetchTopics();
    fetchFreshness();
  }, [fetchTopics, fetchFreshness]);

  // -------------------------------------------------------------------------
  // Actions: Seed & Ingest
  // -------------------------------------------------------------------------
  const handleSeed = async () => {
    setSeedLoading(true);
    setSeedResult(null);
    try {
      const res = await apiClient.post('/provider/legal/seed', {
        bundesland,
        json_filename: 'stadtki_rechtsthemen_bayern_100.json',
      });
      const data = res.data;
      const msg = `Seed abgeschlossen: ${data.inserted ?? 0} eingefügt, ${data.skipped ?? 0} übersprungen (${data.total ?? '?'} gesamt)`;
      setSeedResult({ ok: true, message: msg });
      toast.success(msg);
      fetchTopics();
      fetchFreshness();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Fehler beim Seeden';
      setSeedResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setSeedLoading(false);
    }
  };

  const handleIngest = async () => {
    setIngestLoading(true);
    setIngestResult(null);
    try {
      const res = await apiClient.post('/provider/legal/ingest', { bundesland });
      const data = res.data;
      const msg = `Ingestion abgeschlossen: ${data.embedded ?? 0} Chunks eingebettet, ${data.deleted ?? 0} alte entfernt`;
      setIngestResult({ ok: true, message: msg });
      toast.success(msg);
      fetchFreshness();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Fehler bei der Ingestion';
      setIngestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setIngestLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Smart Paste
  // -------------------------------------------------------------------------
  const handleSmartParse = async () => {
    if (!smartRawText.trim() || !smartQuestion.trim()) {
      toast.error('Bitte Recherche-Text und Originalfrage ausfüllen');
      return;
    }
    setSmartParsing(true);
    try {
      const res = await apiClient.post('/provider/legal/topics/parse', {
        raw_text: smartRawText,
        original_question: smartQuestion,
        bundesland,
      });
      const parsed = res.data?.topic ?? res.data;
      setForm((prev) => ({
        ...prev,
        id: parsed.id || prev.id,
        thema: parsed.thema || '',
        kategorie: parsed.kategorie || '',
        keywords: Array.isArray(parsed.keywords) ? parsed.keywords.join(', ') : (parsed.keywords || ''),
        rechtsgrundlage: parsed.rechtsgrundlage || '',
        zusammenfassung: parsed.zusammenfassung || '',
        einschraenkungen: Array.isArray(parsed.einschraenkungen) ? parsed.einschraenkungen.join('\n') : (parsed.einschraenkungen || ''),
        gemeinde_aktion: parsed.gemeinde_aktion || '',
        url_gesetz: parsed.url_gesetz || '',
        url_weiterfuehrend: parsed.url_weiterfuehrend || '',
        ebene: parsed.ebene || 'mixed',
        status: 'draft',
        bundesland,
      }));
      setSheetTab('manual');
      toast.success('Text erfolgreich strukturiert – bitte Felder prüfen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Strukturieren');
    } finally {
      setSmartParsing(false);
    }
  };

  // -------------------------------------------------------------------------
  // Create / Update / Archive
  // -------------------------------------------------------------------------
  const buildTopicPayload = (targetStatus) => {
    const payload = {
      ...form,
      status: targetStatus,
      keywords: form.keywords
        ? form.keywords.split(',').map((k) => k.trim()).filter(Boolean)
        : [],
      einschraenkungen: form.einschraenkungen
        ? form.einschraenkungen.split('\n').map((e) => e.trim()).filter(Boolean)
        : [],
    };
    return payload;
  };

  const handleSave = async (targetStatus) => {
    setFormSaving(true);
    try {
      const payload = buildTopicPayload(targetStatus);
      if (sheetMode === 'create') {
        if (!payload.id || !payload.thema) {
          toast.error('ID und Thema sind Pflichtfelder');
          setFormSaving(false);
          return;
        }
        await apiClient.post('/provider/legal/topics', payload);
        toast.success('Thema erstellt');
      } else {
        const { id, bundesland: bl, ...updateFields } = payload;
        await apiClient.put(`/provider/legal/topics/${form.id}?bundesland=${bundesland}`, updateFields);
        toast.success('Thema aktualisiert');
      }
      setSheetOpen(false);
      fetchTopics();
      fetchFreshness();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setFormSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await apiClient.delete(`/provider/legal/topics/${archiveTarget}?bundesland=${bundesland}`);
      toast.success('Thema archiviert');
      setArchiveDialogOpen(false);
      setArchiveTarget(null);
      fetchTopics();
      fetchFreshness();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Archivieren');
    } finally {
      setArchiving(false);
    }
  };

  const openCreate = () => {
    setSheetMode('create');
    setForm({ ...EMPTY_FORM, bundesland });
    setSmartRawText('');
    setSmartQuestion('');
    setSheetTab('smart-paste');
    setSheetOpen(true);
  };

  const openEdit = (topic) => {
    setSheetMode('edit');
    setForm({
      id: topic.id || '',
      thema: topic.thema || '',
      kategorie: topic.kategorie || '',
      keywords: Array.isArray(topic.keywords) ? topic.keywords.join(', ') : '',
      rechtsgrundlage: topic.rechtsgrundlage || '',
      zusammenfassung: topic.zusammenfassung || '',
      einschraenkungen: Array.isArray(topic.einschraenkungen) ? topic.einschraenkungen.join('\n') : '',
      gemeinde_aktion: topic.gemeinde_aktion || '',
      url_gesetz: topic.url_gesetz || '',
      url_weiterfuehrend: topic.url_weiterfuehrend || '',
      ebene: topic.ebene || 'mixed',
      status: topic.status || 'draft',
      bundesland: topic.bundesland || bundesland,
    });
    setSheetTab('manual');
    setSheetOpen(true);
  };

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  const formatDate = (dateStr) => {
    if (!dateStr) return '–';
    try {
      return new Date(dateStr).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const filteredTopics = topics;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Header — match Dokumente tab: explicit slate/sky */}
      <div className="flex justify-between items-center">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Landesrecht-Wissensbasis
        </h2>
        <Button
          variant="outline"
          onClick={() => { fetchTopics(); fetchFreshness(); }}
          disabled={topicsLoading || freshnessLoading}
          className={isDark ? '!border-slate-700 !bg-slate-900 !text-slate-300 hover:!bg-slate-800 hover:!text-white' : '!border-slate-300 !bg-white !text-slate-700 hover:!bg-slate-100 hover:!text-slate-900'}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${(topicsLoading || freshnessLoading) ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* ================================================================= */}
      {/* Section 1: Bundesland + Actions — match Dokumente card/buttons */}
      {/* ================================================================= */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>Bundesland & Aktionen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Bundesland selector */}
            <div className="w-56">
              <Label className={isDark ? 'text-slate-200' : 'text-slate-700'}>Bundesland</Label>
              <Select value={bundesland} onValueChange={setBundesland}>
                <SelectTrigger className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}>
                  {BUNDESLAND_OPTIONS.map((bl) => (
                    <SelectItem key={bl.value} value={bl.value}>
                      {bl.label} ({bl.value})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Secondary: outline + inline style so nothing can override */}
            <Button
              onClick={handleSeed}
              disabled={seedLoading}
              variant="outline"
              className={isDark ? '!border-slate-700 !bg-slate-900 !text-slate-300 hover:!bg-slate-800 hover:!text-white' : '!border-slate-300 !bg-white !text-slate-700 hover:!bg-slate-100 hover:!text-slate-900'}
              style={isDark ? { borderColor: 'var(--tw-border-opacity, 1) rgb(51 65 85)', backgroundColor: 'rgb(15 23 42)', color: 'rgb(203 213 225)' } : { borderColor: 'rgb(203 213 225)', backgroundColor: '#fff', color: 'rgb(51 65 85)' }}
            >
              {seedLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Aus JSON seeden
            </Button>
            {/* Primary: inline style so theme cannot override */}
            <Button
              onClick={handleIngest}
              disabled={ingestLoading}
              variant="outline"
              className="!border-0 !bg-sky-600 hover:!bg-sky-500 !text-white"
              style={{ backgroundColor: '#0284c7', color: '#fff', border: 'none' }}
            >
              {ingestLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              In Vector Store ingestieren
            </Button>
          </div>

          {/* Seed result feedback */}
          {seedResult && (
            <div className={`text-sm rounded-lg p-3 border ${seedResult.ok ? 'border-emerald-500/30 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' : 'border-destructive/30 bg-destructive/10 text-destructive'}`}>
              {seedResult.message}
            </div>
          )}

          {/* Ingest result feedback */}
          {ingestResult && (
            <div className={`text-sm rounded-lg p-3 border ${ingestResult.ok ? 'border-emerald-500/30 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' : 'border-destructive/30 bg-destructive/10 text-destructive'}`}>
              {ingestResult.message}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Section 2: Freshness Overview */}
      {/* ================================================================= */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className={`text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>Aktualitäts-Übersicht</CardTitle>
            {freshnessLoading && <Loader2 className={`w-4 h-4 animate-spin ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />}
          </div>
        </CardHeader>
        <CardContent>
          {freshness ? (
            <div className="space-y-3">
              <div className="flex gap-3 flex-wrap">
                <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700 text-sm px-3 py-1">
                  Aktuell: {freshness.green?.count ?? freshness.green?.length ?? 0}
                </Badge>
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700 text-sm px-3 py-1">
                  Prüfen: {freshness.yellow?.count ?? freshness.yellow?.length ?? 0}
                </Badge>
                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700 text-sm px-3 py-1">
                  Veraltet: {freshness.red?.count ?? freshness.red?.length ?? 0}
                </Badge>
              </div>

              {/* Expandable details */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFreshnessExpanded(!freshnessExpanded)}
                className={isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}
              >
                {freshnessExpanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                {freshnessExpanded ? 'Weniger anzeigen' : 'Details anzeigen'}
              </Button>

              {freshnessExpanded && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                  {['green', 'yellow', 'red'].map((bucket) => {
                    const items = freshness[bucket]?.topics ?? freshness[bucket]?.items ?? [];
                    const label = bucket === 'green' ? 'Aktuell' : bucket === 'yellow' ? 'Prüfung nötig' : 'Veraltet';
                    return (
                      <div key={bucket} className={`rounded-md border p-3 ${isDark ? 'border-slate-600 bg-slate-800/50' : 'bg-slate-50 border-slate-200'}`}>
                        <p className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>{label}</p>
                        {items.length === 0 ? (
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Keine Themen</p>
                        ) : (
                          <ul className="space-y-1">
                            {items.slice(0, 10).map((t, i) => (
                              <li key={i} className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                {typeof t === 'string' ? t : (t.thema || t.id || JSON.stringify(t))}
                              </li>
                            ))}
                            {items.length > 10 && (
                              <li className={`text-xs italic ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                … und {items.length - 10} weitere
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {freshnessLoading ? 'Lade Frische-Daten…' : 'Keine Daten verfügbar'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Section 3: Topic List (Table) — match Dokumente card/primary button */}
      {/* ================================================================= */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className={`text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>Themen ({filteredTopics.length})</CardTitle>
            <Button onClick={openCreate} size="sm" variant="outline" className="!border-0 !bg-sky-600 hover:!bg-sky-500 !text-white">
              <Plus className="w-4 h-4 mr-1" />
              Neues Thema
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter row */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="w-52">
              <Select value={filterKategorie} onValueChange={setFilterKategorie}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                  <SelectValue placeholder="Kategorie" />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {KATEGORIE_OPTIONS.map((k) => (
                    <SelectItem key={k} value={k}>{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}>
                  <SelectItem value="all">Alle Status</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          {topicsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className={`w-6 h-6 animate-spin ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
            </div>
          ) : filteredTopics.length === 0 ? (
            <p className={`text-sm text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Keine Themen gefunden
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className={isDark ? 'bg-slate-950' : 'bg-slate-50'}>
                  <TableRow className={isDark ? 'border-slate-700' : 'border-slate-200'}>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>ID</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Thema</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Kategorie</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Status</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Version</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Letzte Prüfung</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTopics.map((topic) => (
                    <TableRow key={topic.id || topic._id} className={isDark ? 'border-slate-700' : 'border-slate-200'}>
                      <TableCell className={`font-mono text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {topic.id}
                      </TableCell>
                      <TableCell className={`max-w-xs truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {topic.thema}
                      </TableCell>
                      <TableCell className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {topic.kategorie || '–'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${STATUS_COLORS[topic.status] || STATUS_COLORS.draft}`}>
                          {STATUS_LABELS[topic.status] || topic.status}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {topic.version ?? '–'}
                      </TableCell>
                      <TableCell className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {formatDate(topic.last_reviewed || topic.updated_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(topic)}
                            className={isDark ? 'text-slate-300 hover:bg-slate-700 hover:text-white' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setArchiveTarget(topic.id); setArchiveDialogOpen(true); }}
                            className={isDark ? 'text-red-400 hover:bg-red-950 hover:text-red-300' : 'text-red-600 hover:bg-red-50 hover:text-red-700'}
                          >
                            <Archive className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Sheet: Create / Edit Topic */}
      {/* ================================================================= */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className={`w-[560px] sm:max-w-[560px] overflow-y-auto [&>button]:opacity-100 ${isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
          <SheetHeader>
            <SheetTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              {sheetMode === 'create' ? 'Neues Thema anlegen' : `Thema bearbeiten: ${form.id}`}
            </SheetTitle>
          </SheetHeader>

          <div className={`mt-6 ${isDark ? 'text-slate-200 [&_label]:text-slate-200 [&_input]:bg-slate-950 [&_input]:border-slate-700 [&_input]:text-white [&_textarea]:bg-slate-950 [&_textarea]:border-slate-700 [&_textarea]:text-white [&_[data-radix-select-trigger]]:bg-slate-950 [&_[data-radix-select-trigger]]:border-slate-700 [&_[data-radix-select-trigger]]:text-white' : 'text-slate-900 [&_label]:text-slate-700 [&_input]:bg-white [&_input]:border-slate-300 [&_input]:text-slate-900 [&_textarea]:bg-white [&_textarea]:border-slate-300 [&_textarea]:text-slate-900 [&_[data-radix-select-trigger]]:bg-white [&_[data-radix-select-trigger]]:border-slate-300 [&_[data-radix-select-trigger]]:text-slate-900'}`}>
            {sheetMode === 'create' && (
              <Tabs value={sheetTab} onValueChange={setSheetTab} className="mb-6">
                <TabsList className={isDark ? 'bg-slate-800' : 'bg-slate-200'}>
                  <TabsTrigger value="smart-paste" className={isDark ? 'text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white' : 'text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900'}>
                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                    Smart Paste
                  </TabsTrigger>
                  <TabsTrigger value="manual" className={isDark ? 'text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white' : 'text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900'}>
                    Manuell
                  </TabsTrigger>
                </TabsList>

                {/* Smart Paste tab */}
                <TabsContent value="smart-paste" className="space-y-4 mt-4">
                  <div>
                    <Label>Recherche-Text einfügen</Label>
                    <Textarea
                      value={smartRawText}
                      onChange={(e) => setSmartRawText(e.target.value)}
                      placeholder="Fügen Sie hier den Text aus Ihrer Recherche ein (Gesetzestext, AI-Zusammenfassung, Notizen…)"
                      className="mt-1 min-h-[180px]"
                    />
                  </div>
                  <div>
                    <Label>Originalfrage</Label>
                    <Input
                      value={smartQuestion}
                      onChange={(e) => setSmartQuestion(e.target.value)}
                      placeholder="z.B. Brauche ich eine Baugenehmigung für ein Gartenhaus?"
                      className="mt-1"
                    />
                  </div>
                  <Button
                    onClick={handleSmartParse}
                    disabled={smartParsing}
                    variant="outline"
                    className="w-full !border-0 !bg-sky-600 hover:!bg-sky-500 !text-white"
                  >
                    {smartParsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Strukturieren
                  </Button>
                </TabsContent>

                {/* Manual tab renders below (shared form) */}
                <TabsContent value="manual" />
              </Tabs>
            )}

            {/* Form (shown in manual tab or edit mode, or after smart parse switches to manual) */}
            {(sheetMode === 'edit' || sheetTab === 'manual') && (
              <div className="space-y-4">
                {/* ID (only editable on create) */}
                <div>
                  <Label>ID *</Label>
                  <Input
                    value={form.id}
                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                    disabled={sheetMode === 'edit'}
                    placeholder="z.B. by_baugenehmigung_gartenhaus"
                    className={sheetMode === 'edit' ? 'opacity-60' : ''}
                  />
                </div>

                {/* Thema */}
                <div>
                  <Label>Thema *</Label>
                  <Input
                    value={form.thema}
                    onChange={(e) => setForm({ ...form, thema: e.target.value })}
                    placeholder="Baugenehmigung für Gartenhaus in Bayern"
                    className="mt-1"
                  />
                </div>

                {/* Kategorie */}
                <div>
                  <Label>Kategorie</Label>
                  <Select value={form.kategorie || ''} onValueChange={(v) => setForm({ ...form, kategorie: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Kategorie wählen" />
                    </SelectTrigger>
                    <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-300'}>
                      {KATEGORIE_OPTIONS.map((k) => (
                        <SelectItem key={k} value={k}>{k}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Keywords */}
                <div>
                  <Label>Keywords (kommagetrennt)</Label>
                  <Input
                    value={form.keywords}
                    onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                    placeholder="Gartenhaus, Baugenehmigung, Bayern"
                    className="mt-1"
                  />
                </div>

                {/* Rechtsgrundlage */}
                <div>
                  <Label>Rechtsgrundlage</Label>
                  <Input
                    value={form.rechtsgrundlage}
                    onChange={(e) => setForm({ ...form, rechtsgrundlage: e.target.value })}
                    placeholder="Art. 57 Abs. 1 Nr. 1 BayBO"
                    className="mt-1"
                  />
                </div>

                {/* Zusammenfassung */}
                <div>
                  <Label>Zusammenfassung</Label>
                  <Textarea
                    value={form.zusammenfassung}
                    onChange={(e) => setForm({ ...form, zusammenfassung: e.target.value })}
                    placeholder="Kurze Zusammenfassung der Rechtslage…"
                    className="mt-1 min-h-[100px]"
                  />
                </div>

                {/* Einschraenkungen */}
                <div>
                  <Label>Einschränkungen (je Zeile eine)</Label>
                  <Textarea
                    value={form.einschraenkungen}
                    onChange={(e) => setForm({ ...form, einschraenkungen: e.target.value })}
                    placeholder={"Nur im Innenbereich\nBebauungsplan beachten"}
                    className="mt-1 min-h-[80px]"
                  />
                </div>

                {/* Gemeinde-Aktion */}
                <div>
                  <Label>Gemeinde-Aktion</Label>
                  <Input
                    value={form.gemeinde_aktion}
                    onChange={(e) => setForm({ ...form, gemeinde_aktion: e.target.value })}
                    placeholder="Bauamt kontaktieren"
                    className="mt-1"
                  />
                </div>

                {/* URL Gesetz */}
                <div>
                  <Label>URL Gesetzestext</Label>
                  <Input
                    value={form.url_gesetz}
                    onChange={(e) => setForm({ ...form, url_gesetz: e.target.value })}
                    placeholder="https://www.gesetze-bayern.de/…"
                    className="mt-1"
                  />
                </div>

                {/* URL Weiterführend */}
                <div>
                  <Label>URL Weiterführend</Label>
                  <Input
                    value={form.url_weiterfuehrend}
                    onChange={(e) => setForm({ ...form, url_weiterfuehrend: e.target.value })}
                    placeholder="https://…"
                    className="mt-1"
                  />
                </div>

                {/* Ebene */}
                <div>
                  <Label>Ebene</Label>
                  <Select value={form.ebene} onValueChange={(v) => setForm({ ...form, ebene: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-300'}>
                      {EBENE_OPTIONS.map((e) => (
                        <SelectItem key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action buttons — force styles to beat theme */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => handleSave('draft')}
                    disabled={formSaving}
                    variant="outline"
                    className={isDark ? 'flex-1 !border-slate-700 !bg-slate-900 !text-slate-300 hover:!bg-slate-800' : 'flex-1 !border-slate-300 !bg-white !text-slate-700 hover:!bg-slate-100'}
                  >
                    {formSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Als Entwurf speichern
                  </Button>
                  <Button
                    onClick={() => handleSave('active')}
                    disabled={formSaving}
                    variant="outline"
                    className="flex-1 !border-0 !bg-sky-600 hover:!bg-sky-500 !text-white"
                  >
                    {formSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Aktivieren
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ================================================================= */}
      {/* Archive Confirmation Dialog */}
      {/* ================================================================= */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Thema archivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Das Thema <span className="font-mono font-medium">{archiveTarget}</span> wird als archiviert markiert und aus dem Vector Store entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={archiving}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {archiving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Archivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
